import { PaymentGoalContext } from '../../../../../../payment-goal-context';
import {reachGoal} from 'utils/metrica';
import {formatReferenceCopy} from 'utils/typography';
import React from "react";

import {useTranslation} from "react-i18next";
import {useFieldArray, useFormContext} from "react-hook-form";
import {useDispatch} from "react-redux";
import isEmpty from "lodash/isEmpty";

import Button from "@mui/material/Button";
import { Check as CheckIcon } from '@vedmarket/ui-kit';

import {
  useSetCompletedByOrderIdMutation,
  useUpdateBankCommissionsMutation
} from "../../../../../../../../../../../api/calc";

import { Completed } from '@vedmarket/ui-kit';
import MenuButton from '../../../../../../../../../../common/menuButton';
import {ADD_PAYMENT_FORM, INITIAL_VALUES_PAYMENT, PaymentsType} from "../../index";
import PaymentItemEdit from "./components/payment-item-edit";
import Notification, {NOTIFICATION_TYPE} from "../../../../../../../../../../common/notification";
import AlertBottom from "../../../../../../../../../../common/alertBottom";
import {useGetCalcIdFromRoute} from "../../../../../../../../../../../utils/hooks";
import ModalBig from "../../../../../../../../../../common/modalBig";
import {
  useUpdateBankCommissionsByInvoiceId,
} from "../../../../../../../../../../../api/calc";
import {compareBankCommissions, isCalculationViewModeError, numberWithSpaces} from "../../../../../../../../../../../utils/helpers";
import {useCalculationReadOnly} from '../../../../../../../../calculation-read-only';

import PaymentItem from "../payment-item";
import PaymentTabs from '../payment-tabs';
import CustomTabPanel from '../custom-tab-panel';

type Props = {
    bankCommissions: BankCommissions[]
    initialValues: any
    invoiceAmount: number
    invoiceId: string
    invoiceNumber: string
    refetch(): void
}

const CompletedInfo = ({
  bankCommissions,
  initialValues,
  invoiceAmount,
  invoiceId,
  refetch,
  invoiceNumber
}: Props) => {

  const { t } = useTranslation();
  const { fields: goalFields, report: reportGoalFields } = React.useContext(PaymentGoalContext);

  const copy = (key: string) => formatReferenceCopy(t(key));
  const readOnly = useCalculationReadOnly()

  const [ tabValue, setTabValue ] = React.useState(0);

  const [ formIsReady, setFormIsReady ] = React.useState(true);

  const [ openAlert, setOpenAlert ] = React.useState(false);

  const [ errorText, setErrorText ] = React.useState('');

  const [ showModal, setShowModal ] = React.useState(false);

  const [ shouldShowSubmitButton, setShouldShowSubmitButton ] = React.useState(false);

  const [ currentIndex, setCurrentIndex ] = React.useState(0);

  const [ addButtonHasClicked, setAddButtonHasClicked ] = React.useState<boolean>(false);

  const {  control, getValues, watch, handleSubmit, formState: { errors } } = useFormContext<PaymentsType>()

  const [ saveBankCommissions ] = useUpdateBankCommissionsMutation()

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()

  const orderId = useGetCalcIdFromRoute()

  const dispatch = useDispatch()

  const { fields, append, remove, replace } = useFieldArray({
    control,
    // @ts-ignore
    name: 'payments',
  });

  const excludedFields = [
    ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE,
    ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE,
    ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE,
    ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE,
  ];

  const sortedBankCommissionsInvoices = bankCommissions.slice().sort(compareBankCommissions);

  React.useEffect(() => {
    const payment = bankCommissions.slice().sort(compareBankCommissions)[tabValue];

    if (!payment) return;

    reportGoalFields({
      commission_percentage: payment.commission_percentage,
      payment_amount: payment.payment_amount,
      bank_transfer_fee: payment.bank_transfer_fee_usd ?? payment.bank_transfer_fee,
      currency_control_fee: payment.currency_control_fee_usd ?? payment.currency_control_fee,
    });
  }, [ bankCommissions, tabValue, reportGoalFields ]);

  const allFieldsFilled = (item: Record<string, any>) => {
    return Object.entries(item)
    // @ts-ignore
      .filter(([ key ]) => !excludedFields.includes(key))
      .every(([ , value ]) => value !== "");
  };

  const allItemsFilled = () => {
    const allPayments = getValues().payments;
    return allPayments.every((payment) => allFieldsFilled(payment));
  };

  watch(() => {
    if (addButtonHasClicked){
      setFormIsReady(allItemsFilled());
    }
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const onSubmit = handleSubmit (async (values) => {
    let shouldUpdateCompleted = true;

    try {

      const newBankCommissions = [
        ...sortedBankCommissionsInvoices,
        ...(addButtonHasClicked ? values.payments : []),
      ];

      const response: any = sortedBankCommissionsInvoices && await saveBankCommissions({
        invoiceId,
        payments: newBankCommissions
      })

      if (isCalculationViewModeError(response)) {
        shouldUpdateCompleted = false;
        return;
      }

      if (response?.error) {
        setErrorText(response.error.data.error || copy("common__text__error"));
      } else {
        setErrorText('')
        setOpenAlert(true)
        refetch()
        setAddButtonHasClicked(false);
        replace([ INITIAL_VALUES_PAYMENT ]);
        setShouldShowSubmitButton(false);
      }
    } catch (error) {
      setErrorText(copy("common__text__error"));
    } finally {
      if (shouldUpdateCompleted) {
        setCompleted({
          orderId,
          banking_commissions: false,
          additional_services: false
        })
      }
    }
  })

  const fieldsItems = [
    ...sortedBankCommissionsInvoices,
    ...(addButtonHasClicked ? fields : []),
  ];

  const paymentFormIsInvalid = !formIsReady || !isEmpty(errors);

  const saveButtonIsDisabled = readOnly || (addButtonHasClicked && paymentFormIsInvalid);

  const handleRemovePaymentFromArray = (index: number) => {
    if (fields.length == index + 1) {
      setTabValue(fieldsItems.length - 2);
    }

    remove(index);
    setShouldShowSubmitButton(true);
  }

  const handleRemove = (index: number, id?: number) => {

    if (!id){
      return;
    }

    const dataToSend = {
      orderId,
      invoiceNumber,
      data: {
        id
      },
      isRemove: true
    }

    // @ts-ignore
    dispatch(useUpdateBankCommissionsByInvoiceId(dataToSend));

    if (index === fieldsItems.length - 1){
      setTabValue(fieldsItems.length - 2);
    }
  }

  const handleRemoveEdit = (index: number, id?: number) => {
    handleRemove(index, id);
    setShowModal(false);
  }

  const handleClickNewButton = () =>{
    setAddButtonHasClicked(true);
    setFormIsReady(allItemsFilled());
    setTabValue(fieldsItems.length);
    setShouldShowSubmitButton(true);
  }

  const handleClosePaymentItem = () => {
    setShowModal(false);
    setShouldShowSubmitButton(true);
  }

  const handleRemovePaymentItem = (index: number, id?: number) => {
    handleRemoveEdit(index, id);
    setShouldShowSubmitButton(true);
  }

  return (
    <>
      <div className='payments-tabs'>
        <PaymentTabs
          tabValue={tabValue}
          handleChange={handleChange}
          fields={fieldsItems}
          startFieldsIndex={sortedBankCommissionsInvoices.length}
          getValues={getValues}
          append={append}
          initialValues={initialValues}
          onClickNewButton={handleClickNewButton}
          shouldAppendItem={addButtonHasClicked}
        />
        {sortedBankCommissionsInvoices.map((field, index) => {

          const completedInfo = [
            {
              label: `${copy('calc__text__bankCommissions__payment')} ${field.commission_percentage}%`,
              value: `${numberWithSpaces(field.payment_amount)} USD`,
            },
            {
              label: copy('calc__text__bankCommissions__bankCommissionForTheTransfer'),
              value: `${field.bank_transfer_fee_usd != null
                ? `${numberWithSpaces(field.bank_transfer_fee_usd.toFixed(2))} USD`
                : `${field.bank_transfer_fee}%`
              }`,
            },
            {
              label: copy('calc__text__bankCommissions__currencyControlCommission'),
              value: `${field.currency_control_fee_usd != null
                ? `${numberWithSpaces(field.currency_control_fee_usd.toFixed(2))} USD`
                : `${field.currency_control_fee}%`
              }`,
            },
          ]

          const handleEdit = () => {
            setShowModal(true);
            setCurrentIndex(index);
          }

          const handleRemoveFromCompletedItem = (index: number, id?: number) => {
            handleRemove(index, id);
            setShouldShowSubmitButton(true);
          }

          return <CustomTabPanel value={tabValue} index={index} key={index}>
            <div className='payments-tabs-completed'>
              <Completed
                info={completedInfo}
                caption={`${copy('calc__text__bankCommissions__payment')} ${index + 1} ${copy('common__text__of')} ${fieldsItems.length}`}
                isFullRow
                onEdit={handleEdit}
                MenuComponent={MenuButton}
                onRemove={sortedBankCommissionsInvoices.length == 1 ?  undefined : () => handleRemoveFromCompletedItem(index, field.id)}
              />
            </div>
          </CustomTabPanel>
        })}
        {fields.map((field, index) => {
          const isFirstItem = index === 0;
          return (
            <CustomTabPanel value={tabValue} index={sortedBankCommissionsInvoices.length + index} key={sortedBankCommissionsInvoices.length + index}>
              <PaymentItem
                control={control}
                index={index}
                isFirstItem={isFirstItem}
                invoiceAmount={invoiceAmount}
                remove={handleRemovePaymentFromArray}
              />
            </CustomTabPanel>
          )
        })}
      </div>
      {shouldShowSubmitButton && <Button
        variant='contained'
        fullWidth
        startIcon={<CheckIcon/>}
        disabled={saveButtonIsDisabled}
        onClick={(event) => {
          reachGoal({
            goal: 'platezhi_po_schotu',
            params: goalFields,
          });
          onSubmit(event);
        }}
        sx={{marginTop: 3}}
      >{`${copy('calc__text__bankCommissions__savePayments')} №${invoiceNumber}`}</Button>}
      {errorText && <Notification
        type={NOTIFICATION_TYPE.WARNING}
        title={copy('common__message__warning')}
        text={errorText}
        sx={{marginTop: 3}}
        noCloseButton
      />}
      {openAlert && (
        <AlertBottom
          open={openAlert}
          onClose={() => setOpenAlert(false)}
          message={copy('common__message__dataSavedSuccessfully')}
        />
      )}
      <ModalBig
        isOpen={showModal}
        onClose={()=> setShowModal(false)}
        title={`${copy('calc__text__bankCommissions__editPayment')}-${currentIndex + 1}, ${copy('common__text__invoice')} № ${invoiceNumber}` }
      >
        <PaymentItemEdit
          index={currentIndex}
          isFirstItem={true}
          invoiceAmount={invoiceAmount}
          remove={() => handleRemovePaymentItem(currentIndex, sortedBankCommissionsInvoices[currentIndex].id)}
          onClose={handleClosePaymentItem}
          invoiceNumber={invoiceNumber}
          data={sortedBankCommissionsInvoices[currentIndex]}
        />
      </ModalBig>
    </>
  );
}

export default CompletedInfo
